import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { HttpResponse } from "@angular/common/http";
import { Subscription, Observable } from "rxjs";
import { tap, mergeMap } from "rxjs/operators";
import { LoaderService } from "../../../../common/services/loader.service";
import { EDiscoveryService } from "../../../services/eDiscovery.service";
import { eDiscoveryRequestSearchSpec, eDiscoveryDocumentSearchSpec, eDiscoveryStatusSearchType, eDiscoveryRequest, eDiscoveryRequestStatus } from "../../../models/eDiscovery.model";
import { OnBaseDocumentDetails, parseFileNameFromHeaders } from '../../../../common/models/onBaseIntegrationAndMapping.model';
import { Organization } from "../../../../common/models/organization.model";
import { WorkOrderWithAssignments, WorkOrderType } from "../../../../common/models/work-order.model";
import { WorkOrderService } from "../../../../common/services/work-order.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { OrganizationService } from '../../../../common/services/organization.service';
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";


@Component({
    selector: 'app-request-details',
    templateUrl: './request-details.component.html',
    styleUrls: ['./request-details.component.scss'],
    providers: [WorkOrderService, OrganizationService, EDiscoveryService],
    standalone: false
})
export class EDiscoveryRequestDetailsComponent implements OnInit, OnDestroy {   

    get appDataDocument(): OnBaseDocumentDetails {
        return this.documents.find(d => d.documentTypeName.toLocaleLowerCase().includes("application data"));
    }

    get appNotesDocument(): OnBaseDocumentDetails {
        return this.documents.find(d => d.documentTypeName.toLocaleLowerCase().includes("application notes"));
    }

    get contactNotesDocument(): OnBaseDocumentDetails {
        return this.documents.find(d => d.documentTypeName.toLocaleLowerCase().includes("contact notes"));
    }

    get fullRequestDocument(): OnBaseDocumentDetails {
        return this.documents.find(d => d.documentTypeName.toLocaleLowerCase().includes("request document"));
    }

    get organizationNotesDocument(): OnBaseDocumentDetails {
        return this.documents.find(d => d.documentTypeName.toLocaleLowerCase().includes("organization notes"));
    }

    get workOrdersDocument(): OnBaseDocumentDetails {
        return this.documents.find(d => d.documentTypeName.toLocaleLowerCase().includes("work orders"));
    }

    documents: OnBaseDocumentDetails[] = [];
    documentsLoaded: boolean = false;

    @ViewChild("downloadLink", { static: true }) public downloadLink: ElementRef;

    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("Cancel", false, () => { return; })]);

    organization: Organization;
    organizationWorkOrders: WorkOrderWithAssignments[];
    request: eDiscoveryRequest;
    requestId: string;
    statusType = eDiscoveryRequestStatus;
    subscriptions: Subscription = new Subscription();
    subheader: SubHeader = new SubHeader({ title: "eDiscovery Details for ", buttons: [{ name: "Return to Search", method: () => this.backToSearch() }] });

    get applicationNumbers(): string[] {
        let data: string[] = [];
        if (this.request && (this.request.includeApplicationData || this.request.includeApplicationNotes)) {
            data = this.request.applicationNumbers.split(",");
        }
        return data;
    }

    get contacts(): string[] {
        let data: string[] = [];
        if (this.request && this.request.contacts && this.request.contacts.length > 0) {
            let contactList = this.request.contacts.split(',');
            for (let c = 0; c < contactList.length; c++) {
                let contact = this.organization.contacts.find(co => co.id === contactList[c]);
                if (contact) {
                    data.push(contact.displayName);
                }
            }
        }
        return data;
    }

    get workOrders(): string[] {
        let data: string[] = [];
        if (this.request && this.request.workOrders && this.request.workOrders.length > 0) {
            let reqWorkOrders = this.request.workOrders.split(',');
            if (this.organizationWorkOrders && this.organizationWorkOrders.length > 0) {
                for (let w = 0; w < reqWorkOrders.length; w++) {
                    let workOrder = this.organizationWorkOrders.find(wo => wo.id === reqWorkOrders[w]);
                    if (workOrder) {
                        data.push(workOrder.title);
                    }
                }
            }            
        }
        return data;
    }

    constructor(
        public router: Router,
        private _route: ActivatedRoute,        
        private _loader: LoaderService,
        private _subheaderService: SubHeaderService,
        private _docService: DocumentManagementService,
        private _orgService: OrganizationService,
        private _workOrderService: WorkOrderService,
        private _discoveryService: EDiscoveryService,
        private _dialogService: DynamicDialogService ) { }

    ngOnInit() {
        this._loader.toggleLoader(true);
        this.loadRequestData();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    backToSearch(): void {
        this.router.navigate(["admin", "eDiscovery", "org", this.request.organizationId]);
    }

    download(document: OnBaseDocumentDetails): void {
        this._loader.toggleLoader(true);
        let obs$: Observable<HttpResponse<Blob>> = (document.id != null && document.id > 0)
            ? this._docService.download(document.id)
            : this._docService.downloadFromQueue(document.queueId);
        
        const load$ = obs$.pipe()
            .subscribe(
                (data: HttpResponse<Blob>) => {
                    this._loader.toggleLoader(false);
                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                    const blob = new Blob([data.body], { type: data.body.type });
					const blobUri = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUri;
					anchor.download = fileName;
					anchor.click();
                },
                (error) => {
                    this.handleError(error);
                }
        );
        this.subscriptions.add(load$);
    }

    handleError(error): void {
        console.error(error);
        this._loader.toggleLoader(false);
        this.errorDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.errorDialog);
    }

    loadRequestData(): void {
        var route$ = this._route.params.pipe(
            tap((data) => {
                if (data["requestId"]) {
                    this.requestId = data["requestId"];
                }
            }),           
            mergeMap(data => {
                const spec = new eDiscoveryRequestSearchSpec(eDiscoveryStatusSearchType.All, "");
                spec.requestId = this.requestId;
                this.subheader.title = `${this.subheader.title}${this.requestId}`;
                return this._discoveryService.searchRequests(spec).pipe(
                    tap((requests) => {
                        if (requests && requests.length > 0) {
                            this.request = requests[0];
                        }
                    })
                );
            }),
            mergeMap(data => {
                let orgId = this.request.organizationId;
                return this._orgService.getOrganization(orgId).pipe(tap((org) => {
                    this.organization = org;
                }));
            }),
            mergeMap(data => {
                return this._workOrderService.getOrganizationWorkOrders(this.organization.id, [WorkOrderType.AdHoc]).pipe(tap((orders) => {
                    this.organizationWorkOrders = orders;
                }));
            }),
            mergeMap(data => {
                const docSpec = new eDiscoveryDocumentSearchSpec();
                docSpec.requestId = this.requestId;
                return this._discoveryService.searchDocuments(docSpec).pipe(
                    tap((docs) => {                        
                        if (docs) {
                            this.documents = docs;
                        }
                        this.documentsLoaded = true;
                    }));
            }))         
            .subscribe(
                () => {        
                    this._subheaderService.updateSubHeader(this.subheader);
                    this._loader.toggleLoader(false);
                },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscriptions.add(route$);
    }
}
